import { Article } from '@wix/answers-api';
import { Box, Breadcrumbs, BreadcrumbsItem, Divider } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import React, { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { TLocalesKeys } from '../../../../types';
import { getLast } from '../../../../util/guards';
import { CategoryItemType } from '../../../common/api/getCategories';
import { useCurrentPage } from '../../../common/hooks/useCurrentPage';
import { helpCenterSlice } from '../../../common/reducers/helpCenter';
import {
  selectArticleHeadingVisible,
  selectCategoryPath,
  selectCurrentArticle,
  selectCurrentCategory,
} from '../../../common/selectors/helpCenter';
import { Page } from '../../../common/types';
import { appRoutes } from '../../../routes';
import { renderModel } from '../../../util/renderModel';
import s from './Breadcrumbs.scss';
import { useBreadcrumbsBI } from './useBreadcrumbsBI';

const isLandingPage = renderModel.isLandingPage();
const pagesToDisplay = [Page.Categories, Page.Category, Page.Article];
export const HOME_CATEGORY_ID = '00000000-0000-0000-0000-000000000000';

const buildBreadcrumbs = (
  allHelpTopics: BreadcrumbsItem,
  categoryPath: CategoryItemType[],
  currentArticle?: Article,
): BreadcrumbsItem[] => {
  return [
    allHelpTopics,
    ...categoryPath.map((path) => ({ id: path.id, value: path.name })),
    ...(currentArticle ? [{ id: currentArticle.id, value: currentArticle.title }] : []),
  ];
};

export const HelpCenterBreadcrumbs = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentPage = useCurrentPage();
  const currentCategory = useSelector(selectCurrentCategory);
  const articleHeadingVisible = useSelector(selectArticleHeadingVisible);
  const currentArticle = useSelector(selectCurrentArticle);
  const categoryPath = useSelector(selectCategoryPath(currentCategory?.id, currentArticle?.categoryId));
  const breadcrumbsContainer = useRef<HTMLDivElement>(null);
  const shouldDisplayBreadcrumbs =
    !isLandingPage &&
    !articleHeadingVisible &&
    categoryPath.length !== 0 &&
    pagesToDisplay.includes(currentPage);

  const breadcrumbs = useMemo(() => {
    const allHelpTopics = {
      id: HOME_CATEGORY_ID,
      value: t<TLocalesKeys>('breadcrumbs_all_help_topics'),
    };
    return buildBreadcrumbs(allHelpTopics, categoryPath, currentArticle ?? undefined);
  }, [categoryPath, currentArticle]);
  const { handleBreadcrumbClickBI } = useBreadcrumbsBI({
    breadcrumbsContainer,
    breadcrumbs,
  });

  const activeId = getLast(breadcrumbs)?.id;

  useEffect(() => {
    if (currentPage === Page.Categories) {
      dispatch(helpCenterSlice.actions.setCurrentCategory(null));
    }
  }, [currentPage]);

  const onBreadcrumbClick = (breadcrumb: BreadcrumbsItem) => {
    if (breadcrumb.id === activeId) {
      return;
    }

    handleBreadcrumbClickBI(breadcrumb);

    if (breadcrumb.id === HOME_CATEGORY_ID) {
      dispatch(helpCenterSlice.actions.setCurrentCategory(null));
      return navigate(appRoutes.categories());
    }

    const pathname = appRoutes.categoryById({
      categoryId: breadcrumb.id as string,
    });

    navigate(pathname);
  };

  if (!shouldDisplayBreadcrumbs) {
    return null;
  }

  return (
    <>
      <Box position="relative">
        <div className={s.breadcrumbsContainer} ref={breadcrumbsContainer}>
          <Box flexShrink={0} padding="0 10px 0 10px">
            <Breadcrumbs
              activeId={activeId}
              onClick={onBreadcrumbClick}
              items={breadcrumbs}
              theme="onWhiteBackground"
            />
          </Box>
        </div>
      </Box>

      <Divider />
    </>
  );
};
